/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { Transition } from 'react-transition-group';
import cx from 'classnames';
import { getWindow, StatusIconMap, SvgCloseSmall } from '../utils/index.js';
import '@itwin/itwinui-css/css/toast.css';
import { IconButton } from '../Buttons/index.js';
const isMotionOk = () => { var _a, _b, _c; return (_c = (_b = (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.matchMedia) === null || _b === void 0 ? void 0 : _b.call(_a, '(prefers-reduced-motion: no-preference)')) === null || _c === void 0 ? void 0 : _c.matches; };
/**
 * Generic Toast Component
 * @example
 * <Toast type='persisting' content='Job processing completed.' category='positive' link={{onClick:() => {alert('Link callback')}, title:'View the report'}} />
 * <Toast type='temporary' content='Processing completed.' category='positive' />
 * <Toast type='temporary' content='26 files are available for synchronization.' category='informational' />
 * <Toast type='persisting' content='Job processing error.' category='negative' />
 */
export const Toast = (props) => {
    const { content, category, type = 'temporary', isVisible, link, duration = 7000, hasCloseButton, onRemove, animateOutTo, placementPosition = 'top', } = props;
    const closeTimeout = React.useRef(0);
    const [visible, setVisible] = React.useState(isVisible);
    const [height, setHeight] = React.useState(0);
    const thisElement = React.useRef(null);
    const [margin, setMargin] = React.useState(0);
    const marginStyle = () => {
        if (placementPosition === 'top') {
            return { marginBottom: margin };
        }
        return { marginTop: margin };
    };
    React.useEffect(() => {
        if (type === 'temporary') {
            setCloseTimeout(duration);
        }
        return () => {
            clearCloseTimeout();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [duration, type]);
    React.useEffect(() => {
        setVisible(isVisible);
    }, [isVisible]);
    React.useEffect(() => {
        // if we don't have animateOutTo point and not isVisible, set negative margin to move other toasts up.
        // Close all and close on toasts with no anchor.
        if (!isVisible && !animateOutTo) {
            setMargin(-height);
        }
    }, [isVisible, animateOutTo, setMargin, height]);
    const close = () => {
        clearCloseTimeout();
        // move element up when this element is closed.
        setMargin(-height);
        setVisible(false);
    };
    const setCloseTimeout = (timeout) => {
        const definedWindow = getWindow();
        if (!definedWindow) {
            return;
        }
        closeTimeout.current = definedWindow.setTimeout(() => {
            close();
        }, timeout);
    };
    const clearCloseTimeout = () => {
        var _a;
        (_a = getWindow()) === null || _a === void 0 ? void 0 : _a.clearTimeout(closeTimeout.current);
    };
    const onRef = (ref) => {
        if (ref) {
            const { height } = ref.getBoundingClientRect();
            setHeight(height);
        }
    };
    const calculateOutAnimation = (node) => {
        // calculation translate x and y pixels.
        let translateX = 0;
        let translateY = 0;
        if (animateOutTo && node) {
            const { x: startX, y: startY } = node.getBoundingClientRect(); // current element
            const { x: endX, y: endY } = animateOutTo.getBoundingClientRect(); // anchor point
            translateX = endX - startX;
            translateY = endY - startY;
        }
        return { translateX, translateY };
    };
    return (React.createElement(Transition, { timeout: { enter: 240, exit: animateOutTo ? 400 : 120 }, in: visible, appear: true, unmountOnExit: true, onEnter: (node) => {
            if (isMotionOk()) {
                node.style.transform = 'translateY(15%)';
                node.style.transitionTimingFunction = 'ease';
            }
        }, onEntered: (node) => {
            if (isMotionOk()) {
                node.style.transform = 'translateY(0)';
            }
        }, onExiting: (node) => {
            if (isMotionOk()) {
                const { translateX, translateY } = calculateOutAnimation(node);
                node.style.transform = animateOutTo
                    ? `scale(0.9) translate(${translateX}px,${translateY}px)`
                    : `scale(0.9)`;
                node.style.opacity = '0';
                node.style.transitionDuration = animateOutTo ? '400ms' : '120ms';
                node.style.transitionTimingFunction = 'cubic-bezier(0.4, 0, 1, 1)';
            }
        }, onExited: onRemove }, React.createElement("div", { ref: thisElement, className: 'iui-toast-all', style: {
            height,
            ...marginStyle(),
        } },
        React.createElement("div", { ref: onRef },
            React.createElement(ToastPresentation, { category: category, content: content, link: link, type: type, hasCloseButton: hasCloseButton, onClose: close })))));
};
/**
 * The presentational part of a toast, without any animation or logic.
 * @private
 */
export const ToastPresentation = (props) => {
    const { content, category, type = 'temporary', link, hasCloseButton, onClose, className, ...rest } = props;
    const StatusIcon = StatusIconMap[category];
    return (React.createElement("div", { className: cx(`iui-toast iui-${category}`, className), ...rest },
        React.createElement("div", { className: 'iui-status-area' }, React.createElement(StatusIcon, { className: 'iui-icon' })),
        React.createElement("div", { className: 'iui-message' }, content),
        link && (React.createElement("a", { className: 'iui-toast-anchor', onClick: link.onClick }, link.title)),
        (type === 'persisting' || hasCloseButton) && (React.createElement(IconButton, { size: 'small', styleType: 'borderless', onClick: onClose, "aria-label": 'Close' },
            React.createElement(SvgCloseSmall, null)))));
};
export default Toast;
